export type PpType = 'shopify' | 'stripe' | 'paypal' | 'checkout_champ' | 'adyen' | 'crm_connector';

export interface PPAccount {
  ppAccountId: number;
  shop: string;
  name: string;
  companyName: string;
  companyId: number;
  ppType: PpType;
  brandName: string | null;
  enabled?: boolean;
}

export interface PPAccountIntegration {
  ppAccountId: number;
  name: string;
  secret: string;
  webhookSecret?: string | null;
  ppType: string;
  brandId?: number | null;
  brandName?: string | null;
  brands?: Brand[];
  unmappedMerchantCount?: number;
  loginId?: string;
  driver?: string | null;
  metadata?: Record<string, string | number> | null;
  baseUrl?: string;
  displayApiKey?: string;
  sandboxBaseUrl?: string | null;
  displaySandboxApiKey?: string | null;
  orderCancelEnabled?: boolean;
  markRefundedSupported?: boolean;
  orderTagsSupported?: boolean;
  recordChargebackSupported?: boolean;
  shopUrl?: string;
  isPublicApp?: boolean;
  shopifyAppVersion?: string | null;
  shopifyAppUpdateAvailable?: boolean;
  environment?: string;
  adyenCompanyName?: string;
  canSubmitEvidence?: boolean | null;
  autoRefundEnabled?: boolean;
  autoRefundDryRun?: boolean;
  subscriptionCancelEnabled?: boolean;
  subscriptionCancelDryRun?: boolean;
  externalRefundEnabled?: boolean;
  externalRefundDryRun?: boolean;
  markChargebackEnabled?: boolean;
  markChargebackDryRun?: boolean;
  shopifyOrderTaggingEnabled?: boolean;
  shopifyOrderCancelEnabled?: boolean;
  vampRatesEnabled?: boolean;
  paymentCacheEnabled?: boolean;
  paymentCacheDays?: number | null;
  customerInfoEnabled?: boolean;
  useProxy?: boolean;
  hasWriteOrdersScope?: boolean;
  hasReadCustomersScope?: boolean;
  displayLoopToken?: string | null;
  loopCancellationEnabled?: boolean;
  loopCancellationDryRun?: boolean;
  loopCancellationOnChargebackEnabled?: boolean;
  loopCancellationOnChargebackDryRun?: boolean;
  displayKachingToken?: string | null;
  kachingCancellationEnabled?: boolean;
  kachingCancellationDryRun?: boolean;
  kachingCancellationOnChargebackEnabled?: boolean;
  kachingCancellationOnChargebackDryRun?: boolean;
  refundMaxAmount?: number | null;
  representmentStartDate?: string;
  enabled?: boolean;
  disabledReason?: string | null;
  disabledBy?: number | null;
  disabledByName?: string | null;
}

export type AutoRefundMode = 'off' | 'read_only' | 'refund';

export function getAutoRefundMode(account: PPAccountIntegration): AutoRefundMode {
  if (!account.autoRefundEnabled) return 'off';
  return account.autoRefundDryRun ? 'read_only' : 'refund';
}

export const AutoRefundModeLabel: Record<AutoRefundMode, string> = {
  off: 'Off',
  read_only: 'On (read-only)',
  refund: 'On (refund)',
};

export type ExternalRefundMode = 'off' | 'read_only' | 'refund';

export function getExternalRefundMode(account: PPAccountIntegration): ExternalRefundMode {
  if (!account.externalRefundEnabled) return 'off';
  return account.externalRefundDryRun ? 'read_only' : 'refund';
}

export const ExternalRefundModeLabel: Record<ExternalRefundMode, string> = {
  off: 'Off',
  read_only: 'On (read-only)',
  refund: 'On (refund)',
};

export type MarkChargebackMode = 'off' | 'read_only' | 'mark';

export const MarkChargebackModeLabel: Record<MarkChargebackMode, string> = {
  off: 'Off',
  read_only: 'On (dry run)',
  mark: 'On (mark)',
};

export type CancellationMode = 'off' | 'read_only' | 'cancel';

export const CancellationModeLabel: Record<CancellationMode, string> = {
  off: 'Off',
  read_only: 'On (read-only)',
  cancel: 'On (cancel)',
};

export type LoopCancellationMode = CancellationMode;

export function getLoopCancellationMode(account: PPAccountIntegration): CancellationMode {
  if (!account.loopCancellationEnabled) return 'off';
  return account.loopCancellationDryRun ? 'read_only' : 'cancel';
}

export function getLoopCancellationOnChargebackMode(account: PPAccountIntegration): CancellationMode {
  if (!account.loopCancellationOnChargebackEnabled) return 'off';
  return account.loopCancellationOnChargebackDryRun ? 'read_only' : 'cancel';
}

export type SubscriptionCancelMode = CancellationMode;

export function getSubscriptionCancelMode(account: PPAccountIntegration): CancellationMode {
  if (!account.subscriptionCancelEnabled) return 'off';
  return account.subscriptionCancelDryRun ? 'read_only' : 'cancel';
}

// The admin PATCH body for /api/companies/:companyId/pp-accounts/:ppType/:accountId/admin.
// Each provider's API schema accepts its own subset; the union keeps one shape at the call sites.
export interface PpAccountSettingsPayload {
  autoRefundMode?: AutoRefundMode;
  subscriptionCancelMode?: SubscriptionCancelMode;
  externalRefundMode?: ExternalRefundMode;
  markChargebackMode?: MarkChargebackMode;
  loopCancellationMode?: LoopCancellationMode;
  loopSubscriptionsToken?: string | null;
  loopCancellationOnChargebackMode?: LoopCancellationMode;
  kachingCancellationMode?: CancellationMode;
  kachingCancellationOnChargebackMode?: CancellationMode;
  kachingToken?: string | null;
  paymentCacheEnabled?: boolean;
  paymentCacheDays?: number | null;
  customerInfoEnabled?: boolean;
  useProxy?: boolean;
  metadata?: Record<string, string | number>;
  shopifyOrderTaggingEnabled?: boolean;
  shopifyOrderCancelEnabled?: boolean;
  vampRatesEnabled?: boolean;
  secret?: string;
  orderCancelEnabled?: boolean;
  markRefundedSupported?: boolean;
  orderTagsSupported?: boolean;
  recordChargebackSupported?: boolean;
  apiKey?: string;
  sandboxBaseUrl?: string | null;
  sandboxApiKey?: string | null;
  enabled?: boolean;
  disabledReason?: string | null;
}

export interface ShopifyAppStatus {
  outdatedAccountNames: string[];
}

export interface Brand {
  brandId: number;
  brandName: string;
  billingProfileId: number;
}

export interface AdyenMerchantBrand {
  merchantAccountCode: string;
  name: string | null;
  brandId: number | null;
  missingInAdyen: boolean;
}

export interface CredentialProbeResult {
  label: string;
  outcome: 'ok' | 'permission_denied' | 'auth_failed' | 'unreachable';
}
